
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Product } from '../../domain/types';
import { catalogService } from '../../domain/services/catalogService';
import { partnerService } from '../../domain/services/partnerService';
import { Shop } from '../../domain/types';
import { useCartActions } from '../../state/CartContext';
import { useMarketing } from '../../state/MarketingContext';
import { useAuth } from '../../state/auth';
import { useToast } from '../../state/ToastContext';
import { logger } from '../../infrastructure/logger';
import { cacheManager, CACHE_TTL } from '../../utils/cache';
import { ERROR_MESSAGES } from '../../domain/constants/errors';
import { HorizontalProductList } from '../../components/HorizontalProductList';
import { RotatingBannerZone } from '../../components/RotatingBannerZone';
import { StoriesRail } from '../../components/StoriesRail';
import { SimpleHeader } from '../../components/SimpleHeader';
import { ProductSkeleton } from '../../components/skeletons/ProductSkeleton';
import { ShopsExplorerRail } from '../../components/ShopsExplorerRail';
import { REPOSITORIES, DISPLAY_LIMITS, PAGINATION, UI_ANIMATIONS } from '../../domain/constants'; 

// Imported Sub-Components (Clean Code)
import { HeroCarousel } from './components/home/HeroCarousel';
import { CategoriesRail } from './components/home/CategoriesRail';
import { FlashDealsSection } from './components/home/FlashDealsSection';

// Simple functional components kept here for extreme simplicity
const CollectionsGrid = React.memo(() => {
    const navigate = useNavigate();
    return (
        <div className="px-4 py-6">
            <h3 className="text-lg font-bold text-text-main dark:text-white mb-3">Explorá Categorías</h3>
            <div className="grid grid-cols-2 gap-3 h-48">
                <div 
                    onClick={() => navigate('/catalog', { state: { category: 'Tecnología' } })}
                    className="relative rounded-2xl overflow-hidden cursor-pointer group bg-black"
                >
                    <div className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-110 opacity-70" style={{backgroundImage: 'url("https://images.unsplash.com/photo-1519389950473-47ba0277781c?auto=format&fit=crop&q=80&w=400")'}}></div>
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent flex flex-col justify-end p-3">
                        <span className="text-white font-black text-lg">Tech</span>
                        <span className="text-white/70 text-xs">Lo último</span>
                    </div>
                </div>
                <div className="flex flex-col gap-3">
                    <div 
                        onClick={() => navigate('/catalog', { state: { category: 'Hogar' } })}
                        className="flex-1 relative rounded-2xl overflow-hidden cursor-pointer group bg-black"
                    >
                        <div className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-110 opacity-70" style={{backgroundImage: 'url("https://images.unsplash.com/photo-1513519245088-0e12902e5a38?auto=format&fit=crop&q=80&w=400")'}}></div>
                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent flex items-end p-3">
                            <span className="text-white font-bold text-sm">Deco & Hogar</span>
                        </div>
                    </div>
                    <div 
                        onClick={() => navigate('/catalog', { state: { category: 'Mascotas' } })}
                        className="flex-1 relative rounded-2xl overflow-hidden cursor-pointer group bg-black"
                    >
                        <div className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-110 opacity-80" style={{backgroundImage: 'url("https://images.unsplash.com/photo-1583511655857-d19b40a7a54e?auto=format&fit=crop&q=80&w=400")'}}></div>
                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent flex items-end p-3">
                            <span className="text-white font-bold text-sm">Mascotas</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
});
CollectionsGrid.displayName = 'CollectionsGrid';

const ClubPromoCard = React.memo(() => {
  const navigate = useNavigate();
  return (
    <div className="px-4 py-4">
      <div 
        onClick={() => navigate('/benefits')}
        className="w-full bg-gradient-to-r from-[#1c1917] to-[#292524] rounded-3xl p-6 relative overflow-hidden shadow-xl border border-white/10 cursor-pointer group"
      >
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10"></div>
        <div className="absolute -right-10 -top-10 w-40 h-40 bg-primary/20 rounded-full blur-3xl group-hover:bg-primary/30 transition-colors"></div>
        
        <div className="relative z-10 flex justify-between items-center">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="material-symbols-outlined text-primary filled">diamond</span>
              <span className="text-xs font-bold text-primary uppercase tracking-widest">Club AllTuc</span>
            </div>
            <h3 className="text-2xl font-black text-white leading-none mb-2">Sumate al Club</h3>
            <p className="text-white/60 text-xs font-medium max-w-[180px]">Disfrutá envíos gratis y beneficios pensados para vos.</p>
          </div>
          <div className="w-12 h-12 rounded-full bg-white text-black flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
            <span className="material-symbols-outlined">arrow_forward</span>
          </div>
        </div>
      </div>
    </div>
  );
});
ClubPromoCard.displayName = 'ClubPromoCard';

const SellOnAllTucPromo = React.memo(() => {
  const navigate = useNavigate();
  return (
    <div className="px-4 py-2">
      <div 
        onClick={() => navigate('/planes')}
        className="w-full bg-gradient-to-r from-green-600 via-green-500 to-emerald-500 rounded-3xl p-6 relative overflow-hidden shadow-xl border border-green-400/30 cursor-pointer group"
      >
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10"></div>
        <div className="absolute -right-10 -bottom-10 w-40 h-40 bg-white/10 rounded-full blur-3xl group-hover:bg-white/20 transition-colors"></div>
        
        <div className="relative z-10 flex justify-between items-center">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="material-symbols-outlined text-white filled">storefront</span>
              <span className="text-xs font-bold text-white/90 uppercase tracking-widest">Vende en AllTuc</span>
            </div>
            <h3 className="text-2xl font-black text-white leading-none mb-2">Abre tu tienda</h3>
            <p className="text-white/80 text-xs font-medium max-w-[180px]">Vende tus productos y alcanza miles de clientes en Tucumán.</p>
          </div>
          <div className="w-12 h-12 rounded-full bg-white text-green-600 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
            <span className="material-symbols-outlined">arrow_forward</span>
          </div>
        </div>
      </div>
    </div>
  );
});
SellOnAllTucPromo.displayName = 'SellOnAllTucPromo';

const TrustBanner = React.memo(() => (
    <div className="bg-black/5 dark:bg-white/5 px-4 py-3 flex justify-evenly items-center border-b border-black/5 dark:border-white/5 text-text-secondary dark:text-gray-400">
        <div className="flex flex-col items-center gap-1">
            <span className="material-symbols-outlined text-xs text-green-500">verified_user</span>
            <span className="text-[9px] font-bold leading-none">Compra Protegida</span>
        </div>
        <div className="h-4 w-px bg-black/10 dark:bg-white/10 mx-1"></div>
        <div className="flex flex-col items-center gap-1">
            <span className="material-symbols-outlined text-xs text-blue-500">credit_card</span>
            <span className="text-[9px] font-bold leading-none">Cuotas s/interés</span>
        </div>
        <div className="h-4 w-px bg-black/10 dark:bg-white/10 mx-1"></div>
        <div className="flex flex-col items-center gap-1">
            <span className="material-symbols-outlined text-xs text-primary">local_shipping</span>
            <span className="text-[9px] font-bold leading-none">Envío Gratis</span>
        </div>
    </div>
));
TrustBanner.displayName = 'TrustBanner';

export const HomeScreen = () => {
    const navigate = useNavigate();
    const { addToCart } = useCartActions();
    const { user } = useAuth();
    const { getBannersByZone, flashDeals } = useMarketing();
    const { addToast } = useToast();
    
    const [allProducts, setAllProducts] = useState<Product[]>([]);
    const [usedProducts, setUsedProducts] = useState<Product[]>([]);
    const [shops, setShops] = useState<Shop[]>([]);
    const [loading, setLoading] = useState(true);
    
    // Derived product arrays using useMemo - reduces state management overhead
    const techProducts = useMemo(() => allProducts.filter(p => p.category === 'Tecnología'), [allProducts]);
    const homeProducts = useMemo(() => allProducts.filter(p => p.category === 'Hogar' || p.category === 'Artesanías'), [allProducts]);
    const fashionProducts = useMemo(() => allProducts.filter(p => p.category === 'Moda'), [allProducts]);
    const sportsProducts = useMemo(() => allProducts.filter(p => p.category === 'Deportes'), [allProducts]);
    const petProducts = useMemo(() => allProducts.filter(p => p.category === 'Mascotas' || p.category === 'BARF'), [allProducts]);
    const drinkProducts = useMemo(() => allProducts.filter(p => p.category === 'Bebidas' || p.category === 'Comidas'), [allProducts]);
    const recentProducts = useMemo(() => allProducts.slice(0, DISPLAY_LIMITS.HOME_RECENT_PRODUCTS).reverse(), [allProducts]);
    
    // isMounted ref to prevent state updates on unmounted component
    const isMounted = useRef(true);

    const heroSlides = useMemo(() => {
        const slides = getBannersByZone('hero_slider', user);
        return slides;
    }, [getBannersByZone, user]);

    const populatedCategories = useMemo(() => {
        const set = new Set<string>();
        allProducts.forEach(p => set.add(p.category));
        usedProducts.forEach(p => set.add(p.category));
        return set;
    }, [allProducts, usedProducts]);

    const loadHomeData = useCallback(async () => {
        if (!isMounted.current) return;
        setLoading(true);
        try {
            // Check cache first for products
            const cachedProducts = cacheManager.get<Product[]>('home', 'products');
            const cachedShops = cacheManager.get<Shop[]>('home', 'shops');
            
            if (cachedProducts && cachedShops) {
                // Use cached data - derived products calculated via useMemo
                if (isMounted.current) {
                    setAllProducts(cachedProducts);
                    setShops(cachedShops);
                    setLoading(false);
                }
                return;
            }

            // Fetch fresh data if not cached
            const [activeProducts, usedResponse, shopsResult] = await Promise.all([
                catalogService.getActiveProducts(PAGINATION.FILTERED_RESULTS_LIMIT).catch(e => {
                    logger.error("Error fetching active products", e instanceof Error ? e : new Error(String(e)));
                    return [];
                }),
                catalogService.getUsedProducts(PAGINATION.DEFAULT_PAGE_SIZE).catch(e => {
                    logger.error("Error fetching used products", e instanceof Error ? e : new Error(String(e)));
                    return { products: [], lastDoc: null };
                }),
                partnerService.getAllShops(PAGINATION.FILTERED_RESULTS_LIMIT).catch(e => {
                    logger.error("Error fetching shops", e instanceof Error ? e : new Error(String(e)));
                    return { shops: [], lastDoc: null };
                })
            ]);

            if (!isMounted.current) return;

            const allValid = activeProducts;
            const usedValid = Array.isArray(usedResponse.products) ? usedResponse.products : [];
            const activeShops = Array.isArray(shopsResult.shops) 
                ? shopsResult.shops.filter((s: Shop) => s.status === 'active')
                : [];

            if (allValid.length === 0 && usedValid.length === 0) {
                logger.warn("[HomeScreen] No valid products found");
                if (isMounted.current) {
                    setAllProducts([]);
                    setUsedProducts([]);
                }
            } else {
                if (isMounted.current) {
                    setAllProducts(allValid);
                    setUsedProducts(usedValid);
                    setShops(activeShops);
                    
                    // Cache the data
                    cacheManager.set('home', 'products', allValid, CACHE_TTL.PRODUCTS);
                    cacheManager.set('home', 'shops', activeShops, CACHE_TTL.SHOPS);
                }
            }
        } catch (err) {
            logger.error("[HomeScreen] Unexpected critical error", err instanceof Error ? err : new Error(String(err)));
            if (isMounted.current) {
                    addToast(ERROR_MESSAGES.NETWORK_REQUEST_FAILED, "error");
            }
        } finally {
            if (isMounted.current) {
                setLoading(false);
            }
        }
    }, [addToast]);

    useEffect(() => {
        isMounted.current = true;
        loadHomeData();
        return () => {
            isMounted.current = false;
        };
    }, [addToast, addToCart]);

    const flashDealProducts = useMemo(() => {
        if (!flashDeals?.enabled || !flashDeals?.productIds || flashDeals.productIds.length === 0) return [];
        return allProducts.filter(p => flashDeals.productIds?.includes(p.id));
    }, [flashDeals, allProducts]);

    const handleQuickAdd = useCallback((product: Product) => {
        if (product.stock === 0) return;
        addToCart(product, 1);
        addToast('Agregado al carrito', 'success');
        if(navigator.vibrate) navigator.vibrate(UI_ANIMATIONS?.VIBRATE_DURATION_MS || 50);
    }, [addToCart, addToast]);

    const handleNavigateToLogin = useCallback(() => {
        navigate('/login');
    }, [navigate]);

    const handleNavigateToPlans = useCallback(() => {
        navigate('/planes');
    }, [navigate]);

    const marqueeText = (
        <div className="flex items-center gap-6 px-3 shrink-0">
            <span className="flex items-center gap-1">👋 ¡Hola! Registrate y gana $5.000.</span>
            <span className="w-1 h-1 bg-white/50 rounded-full"></span>
            <span className="flex items-center gap-1">🔒 Compra 100% Segura.</span>
            <span className="w-1 h-1 bg-white/50 rounded-full"></span>
            <span className="flex items-center gap-1">🚚 Envíos a todo Tucumán.</span>
        </div>
    );

    return (
        <div className="relative flex flex-col w-full min-h-full bg-background-light dark:bg-background-dark pb-28">
            
            {!user && (
                <div onClick={handleNavigateToLogin} className="bg-primary text-white text-[10px] font-bold py-1.5 overflow-hidden cursor-pointer relative z-50 flex">
                    <div className="flex animate-marquee w-fit hover:[animation-play-state:paused]">
                        {marqueeText}{marqueeText}{marqueeText}{marqueeText}
                    </div>
                </div>
            )}

            {/* Global Header Integrated */}
<SimpleHeader isHome={true} />

<TrustBanner />

{/* Vendor CTA Banner for non-partners */}
{!user?.partnerId && (
  <div className="px-4 py-3">
    <div
      onClick={handleNavigateToPlans}
      className="bg-gradient-to-r from-green-500 to-emerald-600 rounded-2xl p-4 flex items-center justify-between cursor-pointer shadow-lg hover:shadow-xl transition-all group"
    >
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
          <span className="material-symbols-outlined text-2xl text-white">storefront</span>
        </div>
        <div>
          <p className="text-white font-bold text-sm">¿Tenés productos para vender?</p>
          <p className="text-white/80 text-xs">Abre tu tienda gratis y llega a miles de clientes</p>
        </div>
      </div>
      <button className="bg-white text-green-600 px-4 py-2 rounded-xl text-xs font-bold shadow-md group-hover:scale-105 transition-transform flex items-center gap-1">
        Empezar
        <span className="material-symbols-outlined text-sm">arrow_forward</span>
      </button>
    </div>
  </div>
)}

<div className="w-full space-y-2 pb-0">
                <StoriesRail />

                <div className="px-4">
                    <HeroCarousel slides={heroSlides || []} />
                    
                    <CategoriesRail populatedCategories={populatedCategories} />
                    
                    {flashDeals.enabled && flashDealProducts.length > 0 && (
                        <div className="-mx-4">
                            <FlashDealsSection 
                                products={flashDealProducts} 
                                discount={flashDeals.discountPercentage} 
                                onAdd={handleQuickAdd} 
                            />
                        </div>
                    )}
                </div>

                <div className="px-4 space-y-4 pt-2">
                    {user && (
                        <HorizontalProductList 
                            title={`Seguir comprando para ${(user.name || 'ti').split(' ')[0]}`} 
                            subtitle="Basado en tus visitas recientes" 
                            products={recentProducts.slice(0, DISPLAY_LIMITS.HOME_PRODUCTS_PREVIEW)} 
                            isLoading={loading} 
                            priority={true} 
                            emptyMessage="Aún no has visto productos"
                        />
                    )}

                    <HorizontalProductList 
                        title="Lo último en Tecnología" 
                        subtitle="Date el gusto (y págalo en cuotas). Te lo mereces."
                        icon="devices" 
                        products={techProducts} 
                        isLoading={loading} 
                        seeAllLink="" 
                        priority={!user} 
                        emptyMessage="Próximamente productos de tecnología"
                    />

                    {/* Middle Promo Banner */}
                    <div className="py-2">
                        <RotatingBannerZone zone="middle_promo" />
                    </div>

                    <HorizontalProductList 
                        title="Renueva tu Hogar" 
                        subtitle="Transforma tu espacio en el refugio que siempre soñaste."
                        icon="chair" 
                        products={homeProducts} 
                        isLoading={loading} 
                        bgColor="bg-teal-50/50 dark:bg-teal-900/10" 
                        titleColor="text-teal-800 dark:text-teal-400" 
                    />
                    
{/* Club Promo Card - Internal Ad */}
<div className="-mx-4">
  <ClubPromoCard />
</div>

{/* Sell on AllTuc Promo - Show for non-partners */}
{!user?.partnerId && (
  <div className="-mx-4">
    <SellOnAllTucPromo />
  </div>
)}
                    
                    <HorizontalProductList 
                        title="Moda y Tendencias" 
                        subtitle="Define quién eres sin decir una palabra. Tu estilo, tu historia."
                        icon="styler" 
                        products={fashionProducts} 
                        isLoading={loading} 
                    />

                    <HorizontalProductList 
                        title="Pasión por el Deporte" 
                        subtitle="Todo lo que necesitás para dar tu mejor versión."
                        icon="sports_soccer" 
                        products={sportsProducts} 
                        isLoading={loading} 
                        bgColor="bg-blue-50/50 dark:bg-blue-900/10"
                        titleColor="text-blue-700 dark:text-blue-300"
                    />
                    
                    {/* Bottom Rotating Banner with Animation */}
                    <div className="py-6">
                        <RotatingBannerZone zone="bottom_promo" />
                    </div>

                    <HorizontalProductList 
                        title="Consentí a tu Mascota" 
                        subtitle="Calidad BARF y accesorios premium para el mejor amigo."
                        icon="pets" 
                        products={petProducts} 
                        isLoading={loading} 
                    />

                    <HorizontalProductList 
                        title="Bebidas y Sabores" 
                        subtitle="Brindemos por los buenos momentos."
                        icon="wine_bar" 
                        products={drinkProducts} 
                        isLoading={loading} 
                        bgColor="bg-orange-50/50 dark:bg-orange-900/10"
                        titleColor="text-orange-700 dark:text-orange-300"
                    />

                    <HorizontalProductList 
                        title="Oportunidades Usadas" 
                        subtitle="Verificados por expertos. Calidad de primera, precio de segunda mano." 
                        icon="recycling" 
                        products={usedProducts} 
                        bgColor="bg-purple-50/50 dark:bg-purple-900/10" 
                        titleColor="text-purple-700 dark:text-purple-300" 
                        isLoading={loading} 
                    />
                </div>

                {/* Tiendas Destacadas Section */}
                {shops.length > 0 && (
                    <div className="px-4 py-6">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-lg font-bold text-text-main dark:text-white">Tiendas Destacadas</h2>
                            <button
                                onClick={() => navigate('/tiendas')}
                                className="text-xs font-bold text-primary hover:text-orange-700 dark:hover:text-orange-400 transition-colors"
                            >
                                Ver todas →
                            </button>
                        </div>
                        <div className="flex gap-3 overflow-x-auto no-scrollbar pb-2 -mx-4 px-4">
                            {shops.slice(0, DISPLAY_LIMITS.FEATURED_SHOPS).map(shop => (
                                <button
                                    key={shop.id}
                                    onClick={() => navigate(`/tienda/${shop.slug}`)}
                                    className="flex-shrink-0 w-28 text-center group"
                                >
                                    <div className="w-20 h-20 mx-auto rounded-2xl overflow-hidden border-2 border-gray-200 dark:border-white/10 shadow-sm group-hover:shadow-md group-hover:border-primary/50 transition-all">
                                        {shop.logo ? (
                                            <img src={shop.logo} alt={shop.name} className="w-full h-full object-cover" />
                                        ) : (
                                            <div className="w-full h-full bg-gray-100 dark:bg-white/5 flex items-center justify-center">
                                                <span className="material-symbols-outlined text-gray-400">storefront</span>
                                            </div>
                                        )}
                                    </div>
                                    <p className="mt-2 text-xs font-bold text-text-main dark:text-white line-clamp-1 group-hover:text-primary transition-colors">
                                        {shop.name}
                                    </p>
                                    {shop.stats?.rating && (
                                        <div className="flex items-center justify-center gap-0.5 mt-1">
                                            <span className="material-symbols-outlined text-yellow-500 text-xs filled">star</span>
                                            <span className="text-xs text-gray-500">{shop.stats.rating.toFixed(1)}</span>
                                        </div>
                                    )}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

{/* Shops Explorer Rail - Always visible */}
      <ShopsExplorerRail shops={shops} isLoading={loading} />

      {/* Loading Skeleton */}
      {loading && (
        <div className="px-4 py-6">
          <div className="grid grid-cols-2 gap-3">
            {[...Array(DISPLAY_LIMITS.CATALOG_LOADING)].map((_, i) => (
              <ProductSkeleton key={i} />
            ))}
          </div>
        </div>
      )}

      {/* Collections Grid */}
      <CollectionsGrid />
            </div>
        </div>
    );
};
